import type { ParseKeys } from 'i18next'
import { useTranslation } from 'react-i18next'

import { Field, Select } from '@/components/ui/Field'
import { toOptions } from '@/components/ui/control'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { useSession } from '@/features/auth/api/useSession'
import { useCase } from '@/features/cases/api/useCase'
import { counterpartyRights } from '@/features/counterparties/counterpartyList'
import {
  useAmendPayment,
  useIssuingEntityOptions,
  type PaymentAmendPayload,
} from '@/features/finance/api/useFinance'
import { PaymentPartyPicker } from '@/features/finance/components/PaymentPartyPicker'
import { normaliseDecimal } from '@/features/finance/lines'
import {
  caseClientOf,
  partyComplete,
  partyPayload,
  storedParty,
  type PaymentParty,
} from '@/features/finance/paymentParty'
import { paymentRefusalMessage } from '@/features/finance/paymentRefusal'
import { useFormat } from '@/i18n/useFormat'
import { fieldErrorKey, fieldErrors } from '@/lib/problems'
import type { Payment, PaymentMethod } from '@/types/domain'

/* Correcting a PENDING payment in place — the third fate beside confirm and
 * reject, for the row that records the right money on the right day and
 * mistypes a field of it.
 *
 * WHAT THE FORM DOES NOT OFFER, and why the absence is the design: the
 * currency, the received-on date, the direction and the case are not fields
 * here, because the wire does not carry them (`PaymentAmendPayload`) and the
 * server has no column-move to make (`PaymentAmendment`). The first two are
 * what every booking on the row and its allocations hangs off — amending
 * either would strand settlement figures booked at a date the payment no
 * longer states — so a payment recorded on the wrong day or in the wrong
 * currency is REJECTED with a reason and re-recorded, which re-books
 * everything by construction. The sheet says so in one line over the fields,
 * so the reader is told where the door is rather than left hunting for a
 * disabled box.
 *
 * The AMOUNT may move, but not below what the payment has already settled —
 * the server's below-allocated refusal (AD-11 asked of an edit) comes back on
 * the amount field like any other field problem.
 *
 * THE PARTY (S-22d2) is picked by record under the payment's own direction's
 * rule (`PaymentPartyPicker`), seeded from what the row stores: a counterparty
 * or a client by id — a client who is no longer the case's stays «как
 * записано» — or an old typed name, «как введено». The case is read for its
 * CURRENT client (an ordinary cached query: the leg page already holds it).
 * An outbound row typed before the link existed saves only with its name
 * echoed unchanged; any other change is a pick. When the party is withheld
 * from this reader (AD-6 — the name arrives as ''), the field is shown
 * disabled with why and the request carries NO party field: the server keeps
 * the stored party, and an enabled picker over a party nobody can see would
 * be an invitation to overwrite it.
 */

const METHODS: PaymentMethod[] = ['cash', 'bank_transfer']

const CURRENCIES = ['USD', 'EUR', 'CNY', 'RUB', 'TMT'] as const

function isValidAmount(raw: string): boolean {
  const normalised = normaliseDecimal(raw)
  return normalised !== '' && /^\d+(\.\d{1,2})?$/.test(normalised)
}

/** The party's served codes, under the party field (as the record sheet's). */
const PARTY_FIELD_MESSAGES: Record<string, Record<string, ParseKeys>> = {
  counterpartyId: {
    required: 'finance.payments.party.pickOut',
    invalid: 'common.form.counterpartyUnknown',
    direction: 'common.form.partyDirection',
  },
  clientId: {
    invalid: 'finance.payments.party.clientChanged',
    // S-22f: an outbound payment allocated to costs cannot become a refund.
    allocated: 'finance.payments.party.refundAllocated',
  },
}

interface AmendDraft {
  reference: string
  party: PaymentParty
  method: PaymentMethod
  issuingEntityId: string
  amount: string
  feeAmount: string
  feeCurrency: string
}

export function PaymentAmendSheet({
  open,
  onClose,
  payment,
}: {
  open: boolean
  onClose: () => void
  payment: Payment
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const { data: entities } = useIssuingEntityOptions()
  const amend = useAmendPayment()
  const { data: caseRecord } = useCase(payment.caseId, { enabled: open })
  const { data: session } = useSession()

  /* Withheld from this reader (AD-6): the server serves the party's name as a
     bare '' — never blank on a saved payment otherwise, the column refuses it —
     and serves no id. Without `counterparty.read` the same holds by rule. */
  const partyLocked = payment.counterpartyName === '' || !counterpartyRights(session).read
  const stored = storedParty(payment)
  /* An outbound payment already allocated to costs cannot become a refund
     (S-22f): the server refuses the switch with `clientId: allocated`, so the
     case's client is offered disabled, with those words, before it is sent. */
  const refundRefused =
    payment.direction === 'out' && payment.allocations.length > 0
      ? t('finance.payments.party.refundAllocated')
      : undefined

  const [draft, setDraft, touched, markTouched, dirty] = useFormDraft<AmendDraft>(
    open ? payment.id : null,
    () => ({
      reference: payment.reference,
      party: stored,
      method: payment.method,
      issuingEntityId: payment.issuingEntityId,
      amount: payment.amount.amount,
      // Absent stays absent: seeding '0.00' would turn "no fee was recorded"
      // into a claim the row never made (PaymentFormSheet's own rule).
      feeAmount: payment.fee?.amount ?? '',
      feeCurrency: payment.fee?.currency ?? '',
    }),
  )

  const referenceError =
    touched && !draft.reference.trim() ? t('common.form.required') : undefined
  /* Outbound: a record, or the stored typed name echoed exactly — refused with
     the reason otherwise (S-22d's shape). Inbound: the client, or a typed name
     that is required like any other field. Nothing asked of a locked party. */
  const partyReady = partyLocked || partyComplete(payment.direction, draft.party, stored)
  const pickRequired = payment.direction === 'out' && !partyReady
  const served = fieldErrors(amend.error)
  const servedError = (field: string): string | undefined => {
    const code = served[field]?.[0]
    if (!code) return undefined
    return t(PARTY_FIELD_MESSAGES[field]?.[code] ?? fieldErrorKey(code))
  }
  const partyError =
    (touched && payment.direction === 'in' && !partyReady
      ? t('common.form.required')
      : undefined) ??
    servedError('counterpartyId') ??
    servedError('clientId') ??
    servedError('counterpartyName')
  const entityError =
    touched && !draft.issuingEntityId ? t('common.form.required') : undefined
  const amountError =
    touched && !isValidAmount(draft.amount) ? t('common.form.invalidNumber') : undefined
  const feeTyped = draft.feeAmount.trim() !== ''
  const feeError =
    touched && feeTyped && !isValidAmount(draft.feeAmount)
      ? t('common.form.invalidNumber')
      : undefined

  const invalid =
    !draft.reference.trim() ||
    !partyReady ||
    !draft.issuingEntityId ||
    !isValidAmount(draft.amount) ||
    (feeTyped && !isValidAmount(draft.feeAmount))

  const submit = () => {
    markTouched()
    if (invalid) return

    const payload: PaymentAmendPayload = {
      reference: draft.reference.trim(),
      // A withheld party sends nothing: the server keeps what it stores.
      ...(partyLocked ? {} : partyPayload(draft.party)),
      method: draft.method,
      issuingEntityId: draft.issuingEntityId,
      amount: normaliseDecimal(draft.amount),
      ...(feeTyped
        ? {
            fee: {
              amount: normaliseDecimal(draft.feeAmount),
              currency:
                draft.feeCurrency === ''
                  ? payment.amount.currency
                  : draft.feeCurrency,
            },
          }
        : {}),
    }

    amend.mutate(
      { paymentId: payment.id, caseId: payment.caseId, payload },
      { onSuccess: onClose },
    )
  }

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={t('finance.payments.amendTitle', { reference: payment.reference })}
      submitLabel={t('finance.payments.amendSubmit')}
      pending={amend.isPending}
      dirty={dirty}
      blockedReason={pickRequired ? t('finance.payments.party.pickOut') : undefined}
      error={
        amend.error
          ? paymentRefusalMessage(amend.error, t)
          : touched && invalid
            ? t('common.form.fixFields')
            : undefined
      }
      onSubmit={submit}
    >
      {/* The facts this form cannot move, stated as a sentence rather than as
          four disabled boxes — and where the door is when one of THEM is the
          typo. */}
      <p className="text-text-muted text-[12.5px] leading-snug font-light">
        {t('finance.payments.amendFixedFacts', {
          currency: payment.amount.currency,
          date: format.dateShort(payment.receivedOn),
        })}
      </p>

      <Field label={t('finance.payments.reference')} required error={referenceError}>
        {(props) => (
          <Input
            value={draft.reference}
            onChange={(e) => setDraft({ ...draft, reference: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('finance.balances.counterparty')}
        required
        error={partyError}
        hint={
          partyLocked
            ? t('common.suppressed.reason')
            : pickRequired
              ? t('finance.payments.party.pickOut')
              : payment.direction === 'out'
                ? t('finance.payments.party.hintOut')
                : t('finance.payments.party.hintIn')
        }
      >
        {(props) =>
          /* A withheld party is a disabled box that says so — not the picker,
             whose inbound shape would offer «Другой плательщик» over a payer
             nobody here can see. */
          partyLocked ? (
            <Input value="" placeholder={t('common.suppressed.generic')} disabled {...props} />
          ) : (
            <PaymentPartyPicker
              direction={payment.direction}
              value={draft.party}
              caseClient={caseClientOf(caseRecord)}
              stored={stored}
              refundRefused={refundRefused}
              onChange={(party) => setDraft((current) => ({ ...current, party }))}
              {...props}
            />
          )
        }
      </Field>

      <div className="flex gap-2.5">
        <Field label={t('finance.payments.method')} required className="flex-1">
          {(props) => (
            <Select
              value={draft.method}
              onChange={(method) => setDraft({ ...draft, method })}
              options={toOptions(METHODS, (method) =>
                t(`finance.payments.methods.${method}`),
              )}
              {...props}
            />
          )}
        </Field>

        {/* The currency travels beside the amount but is NOT a control: it is
            the fact the fixed-facts line names, rendered where the reader's eye
            checks it against the figure. */}
        <Field
          label={t('common.fields.amount')}
          required
          error={amountError}
          hint={payment.amount.currency}
          className="flex-1"
        >
          {(props) => (
            <Input
              inputMode="decimal"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>
      </div>

      <Field
        label={t('finance.payments.entity')}
        required
        error={entityError}
        hint={t('finance.payments.entityHint')}
      >
        {(props) => (
          <Select
            value={draft.issuingEntityId}
            onChange={(issuingEntityId) => setDraft({ ...draft, issuingEntityId })}
            options={(entities ?? []).map((entity) => ({
              value: entity.id,
              label: entity.name,
            }))}
            {...props}
          />
        )}
      </Field>

      {/* The fee replaces the stored pair WHOLESALE: the boxes show what the
          row holds and what the reader leaves in them is the truth — an
          emptied pair means "no fee", not "no change". Its currency IS
          amendable, unlike the payment's: a commission is re-read off the
          statement in whatever the bank charged. */}
      <div className="border-line-hair flex flex-col gap-2 border-t pt-3">
        <span className="asa-eyebrow">{t('finance.payments.feeHeading')}</span>

        <div className="flex gap-2.5">
          <Field
            label={t('finance.payments.fee')}
            error={feeError}
            hint={t('finance.payments.feeHint')}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.feeAmount}
                onChange={(e) => setDraft({ ...draft, feeAmount: e.target.value })}
                className="asa-num"
                {...props}
              />
            )}
          </Field>

          <Field label={t('finance.payments.feeCurrency')} className="w-28 shrink-0">
            {(props) => (
              <Select
                value={
                  draft.feeCurrency === '' ? payment.amount.currency : draft.feeCurrency
                }
                onChange={(feeCurrency) => setDraft({ ...draft, feeCurrency })}
                options={toOptions(CURRENCIES, (currency) => currency)}
                {...props}
              />
            )}
          </Field>
        </div>
      </div>
    </FormSheet>
  )
}
